import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { SparklesIcon } from '@heroicons/react/24/outline'
import type { Attachment } from '../../client'
import { collectToolResults, groupSubMessages, messageToolUses } from '../../lib/messageContent'
import MediaPreview from '../home/MediaPreview'
import ChatMessage from './ChatMessage'
import ConnectPrompt from './ConnectPrompt'
import { segments, type Segment } from './segments'
import Spinner from './Spinner'
import StatusBubble from './StatusBubble'
import StreamingTurn from './StreamingTurn'
import PermissionPrompt from './PermissionPrompt'
import PlanCard from './PlanCard'
import SummarizationPrompt from './SummarizationPrompt'
import { useChat } from '../../providers/chat/context'
import { RUN_PREFIX } from '../../lib/workflows'

const STICKY_PX = 80

export default function MessageList() {
  const {
    activeId,
    messages,
    busy,
    error,
    pendingPermission,
    pendingSummarization,
    plans,
    hasMoreMessages,
    loadingMessages,
    loadOlderMessages,
  } = useChat()
  const settled = busy === null && pendingPermission === null && pendingSummarization === null
  const [preview, setPreview] = useState<Attachment | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const stick = useRef(true)

  const { topLevel, subMessages, segsById, results, lastAssistantId } = useMemo(() => {
    const top = messages.filter(({ parent_tool_use_id }) => !parent_tool_use_id)
    const subs = groupSubMessages(messages)

    // One stored message per agent round, so a multi-round turn would stack a reasoning
    // box per round. Runs that meet with nothing rendered between them read as one.
    const byId = new Map<string, Segment[]>()
    let open: Segment | null = null
    for (const message of top) {
      const own = segments(message, subs.get(message.id) ?? [])
      if (open && own[0]?.kind === 'reasoning') {
        open.parts = [...open.parts, ...own[0].parts]
        own.shift()
      }
      const last = own.at(-1)
      open = last?.kind === 'reasoning' ? last : open && own.length === 0 ? open : null
      byId.set(message.id, own)
    }

    return {
      topLevel: top,
      subMessages: subs,
      segsById: byId,
      results: collectToolResults(messages),
      lastAssistantId: top.findLast(({ role }) => role === 'assistant')?.id,
    }
  }, [messages])

  const plansByMessage = useMemo(() => {
    const byToolUse = new Map(plans.map((plan) => [plan.tool_use_id, plan]))
    const byMessage = new Map<string, typeof plans>()
    for (const message of messages) {
      const found = messageToolUses(message.content).flatMap(({ id }) => byToolUse.get(id) ?? [])
      if (found.length > 0) byMessage.set(message.id, found)
    }
    const runs = plans.filter(({ tool_use_id }) => !tool_use_id)
    const starts = messages.filter(
      ({ role, content }) => role === 'user' && typeof content === 'string' && content.startsWith(RUN_PREFIX),
    )
    const offset = starts.length - runs.length
    runs.forEach((plan, index) => {
      const start = starts[index + offset]
      if (start) byMessage.set(start.id, [...(byMessage.get(start.id) ?? []), plan])
    })
    return byMessage
  }, [messages, plans])

  useEffect(() => {
    const element = scrollRef.current
    if (element && stick.current) element.scrollTop = element.scrollHeight
  }, [messages, busy, pendingPermission, pendingSummarization, plans])

  function onScroll() {
    const element = scrollRef.current
    if (!element) return
    stick.current = element.scrollHeight - element.scrollTop - element.clientHeight <= STICKY_PX
  }

  const opening = messages.length === 0 && loadingMessages
  const empty = messages.length === 0 && busy === null && !loadingMessages

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="min-h-0 flex-1 overflow-y-auto px-3 py-3 [scrollbar-width:thin]"
    >
      <div className="mx-auto w-full max-w-3xl">
        {hasMoreMessages && (
          <div className="mb-3 flex justify-center">
            <button
              type="button"
              disabled={loadingMessages}
              onClick={() => void loadOlderMessages()}
              className="text-[11px] text-purple-700 transition hover:text-purple-900 disabled:opacity-50"
            >
              {loadingMessages ? 'Loading…' : 'Load earlier messages'}
            </button>
          </div>
        )}

        {opening ? (
          <div className="flex justify-center py-16">
            <Spinner className="size-5 border-stone-200 border-t-stone-500" />
          </div>
        ) : empty ? (
          <div className="flex flex-col items-center px-4 py-16 text-center text-stone-400">
            <SparklesIcon className="mb-2 size-8" />
            <p className="text-sm font-medium text-stone-600">Ads Assistant</p>
            <p className="mt-1 max-w-[260px] text-xs leading-relaxed">
              Ask me to review your campaigns, launch new ads, spin up competitor research, or explain
              performance.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {topLevel.map((message) => (
              <Fragment key={message.id}>
                <ChatMessage
                  message={message}
                  segs={segsById.get(message.id) ?? []}
                  subMessages={subMessages.get(message.id) ?? []}
                  results={results}
                  showActions={message.id === lastAssistantId && settled}
                  onPreview={setPreview}
                />
                {plansByMessage.get(message.id)?.map((plan) => <PlanCard key={plan.id} plan={plan} />)}
              </Fragment>
            ))}

            <StreamingTurn scrollRef={scrollRef} stick={stick} onPreview={setPreview} />

            {busy === 'summarizing' && <StatusBubble label="Summarizing conversation…" />}

            {settled && <ConnectPrompt key={activeId} />}
            {pendingPermission && <PermissionPrompt key={pendingPermission.state_token} />}
            {pendingSummarization && <SummarizationPrompt />}

            {error && (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-600">
                {error}
              </p>
            )}
          </div>
        )}
      </div>

      <MediaPreview
        src={preview?.url ?? null}
        kind={preview?.kind}
        alt={preview?.original_filename ?? ''}
        onClose={() => setPreview(null)}
      />
    </div>
  )
}
